import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useOutletContext, useParams } from "react-router-dom";
import { useAuth } from 'react-oidc-context';
import { v4 as uuidv4 } from "uuid";

import { useAppDispatch, useAppSelector } from '../../../../app/store';
import { cn } from '../../../../lib/utils';

import { CaseEvent, EventEntityType, EventStatus, EventType } from '../../../../api/events';
import { FinalFICDecision, FinalFICReport } from '../../../../api/finalFIC';
import { DocumentEntityType, DocumentType } from '../../../../api/caseDocuments';
import { showErrorToast, showSuccessToast } from '../../../../lib/toast';

import { selectEventByTypeAndEntityId } from '../../../timeline/logic/slice';
import { isCreditAccountStakeholdersRevampEnabled, isFICIncidentParticularsEnabled } from '../../../configs/logic/Slice';
import { selectCaseHeader } from '../../../caseinfo/logic/Slice';
import { validateExposure } from '../../../caseinfo/logic/validations';
import ReportDocuments from '../../../documentManagement/components/ReportDocuments';

import { selectFinalFICData, selectFinalFICError, selectFinalFICStatus, selectIsFinalFICCreated } from '../logic/slice';
import { createFinalFICMeetingThunk, fetchFinalFICMeetingThunk, updateFinalFICMeetingThunk } from '../logic/thunks';

import CansoEditor from "../../../../components/ui/editor";
import { ErrorDisplay } from "../../../../components/ui/error/ErrorDisplay";
import ReportActionHeader from "../../components/common/ReportActionHeader";
import ReportHeader from "../../components/common/ReportHeader";
import { ReportDetailsShimmering } from "../../components/common/ReportDetailsShimmering";
import ViewOrAddRecommendation from "../../components/common/ViewOrAddRecommendation";

import { FICDecisionCommitteeTable } from "./FICDecisionCommitteeTable";
import { FICIncidentParticularsTable, IncidentParticulars } from "./FICIncidentParticularsTable";
import { FICPartiesInvolvedSection, FICPartiesSectionRef } from "./FICPartyInvolvedSection";
import { FICPartiesInvolvedSectionV2 } from "./FICPartyInvolvedSectionV2";

import RedirectActionsBar from "../../../../components/ui/cards/RedirectActionsBar";
import CommentMainPage from "../../../comments/components/CommentMainPage";
import { CommentEntityType, CommentThreadType } from "../../../../api/comments";

import styles from "../../components/fic/FICPage.module.css";
import { lakhsToRupees, rupeesToLakhs } from '../../../../features/caseinfo/components/FraudAmountOverviewItem';
import { CaseRouteId, getCaseRoutePath } from '../../../../features/caselayout/config/caseRoutes';

const toCommitteeData = (ficData: FinalFICReport, ficEvent?: CaseEvent) => ({
  convener: ficData.convener || "",
  members: ficData.members_of_committee || [],
  date: ficEvent?.date || "",
  decision: ficData.committee_decision,
  committeeName: ficData.name_of_committee || "",
  fraudClassification: ficData.fraud_classification || "",
});

const isDateChanged = (date?: string, storedEvent?: CaseEvent) =>
  !!date && date !== (storedEvent?.date ?? "");

const ReportSectionHeading = ({ index, title }: { index: number; title: string }) => (
  <>
    {index > 1 && <div className={cn(styles.dividerLine, "my-7")} />}

    <div className={styles.textFieldContainer}>
      <span className={styles.textFieldLabel}>
        {index}. {title}
      </span>
    </div>
  </>
);

const FICReportCard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { id: case_id } = useParams<{ id: string }>();

  const ficError = useAppSelector(selectFinalFICError);
  const isLoading = useAppSelector(selectFinalFICStatus) === "loading";

  return (
    <div className={styles.reportCard}>
      {isLoading ? (
        <ReportDetailsShimmering sectionCount={4} />
      ) : ficError ? (
        <ErrorDisplay
          error={ficError}
          title="Failed to load FIC for Final Decision on Fraud or No Fraud"
          onRetry={() => {
            if (case_id) {
              dispatch(fetchFinalFICMeetingThunk({ case_id }));
            }
          }}
        />
      ) : (
        <>
          <ReportHeader title="FIC for Final Decision on Fraud or No Fraud" rootContainerClassName="mt-0" />
          <div className={cn(styles.reportContent, "px-6 py-4 gap-0")}>
            {children}
          </div>
        </>
      )}
    </div>
  );
};

const FICPageFooter: React.FC = () => {
  const { id: case_id } = useParams<{ id: string }>();
  const isLoading = useAppSelector(selectFinalFICStatus) === "loading";

  return (
    <>
      {!isLoading && (
        <div className={styles.redirectActionsBar}>
          <RedirectActionsBar
            primaryAction={{
              text: "View Show Cause Notice",
              url: getCaseRoutePath(case_id!, CaseRouteId.REPORT_SCN),
              openInNewTab: true,
              disabled: false,
            }}
            secondaryAction={{
              text: "View Reasoned Order",
              url: getCaseRoutePath(case_id!, CaseRouteId.REPORT_RO),
              openInNewTab: true,
              disabled: false,
            }}
          />
        </div>
      )}

      {!isLoading && case_id && (
        <div className="w-full mx-auto max-w-[1031px]">
          <CommentMainPage
            caseId={case_id}
            threadType={CommentThreadType.FIC}
            entityId={case_id}
            entityType={CommentEntityType.CASE}
            className="w-full max-w-none mx-0 mt-4"
          />
        </div>
      )}

      <div className="h-14" />
    </>
  );
};


export const FICDecisionPage: React.FC = () => {
  const { scrollRef } = useOutletContext<{
    scrollRef: React.RefObject<HTMLDivElement>;
  }>();

  const partiesRef = useRef<FICPartiesSectionRef>(null);

  const dispatch = useAppDispatch();
  const actorEmail = useAuth().user?.profile?.email ?? "";

  const { id: case_id } = useParams<{ id: string }>();

  const ficData = useAppSelector(selectFinalFICData);
  const isCreated = useAppSelector(selectIsFinalFICCreated);
  const isFicLoading = useAppSelector(selectFinalFICStatus) === "loading";
  const caseExposure = useAppSelector(selectCaseHeader)?.exposure ?? 0;

  const ficEvent = useAppSelector((state) =>
    case_id
      ? selectEventByTypeAndEntityId(EventType.FIC_FRAUD_CLASSIFICATION_COMPLETED, case_id)(state)
      : undefined
  );

  const occurrenceEvent = useAppSelector(selectEventByTypeAndEntityId(EventType.DATE_OF_OCCURENCE, case_id ?? ""));
  const detectionEvent = useAppSelector(selectEventByTypeAndEntityId(EventType.DATE_OF_DETECTION, case_id ?? ""));
  const classificationEvent = useAppSelector(selectEventByTypeAndEntityId(EventType.DATE_OF_CLASSIFICATION_CA_APPROVAL, case_id ?? ""));

  const storedIncident = useMemo<IncidentParticulars>(
      () => ({
          exposure: rupeesToLakhs(caseExposure),
          dateOfOccurrence: occurrenceEvent?.date ?? "",
          dateOfDetection: detectionEvent?.date ?? "",
          dateOfClassificationApproval: classificationEvent?.date ?? "",
      }),
      [caseExposure, occurrenceEvent, detectionEvent, classificationEvent]
  );

  const [commiteeRemarks, setCommiteeRemarks] = useState<string>('');
  const [committeeData, setCommitteeData] = useState({
      convener: "",
      members: [] as string[],
      date: "",
      decision: undefined as FinalFICDecision | undefined,
      committeeName: "", fraudClassification: "",
  });

  const [incident, setIncident] = useState<IncidentParticulars>({});
  const [editorResetKey, setEditorResetKey] = useState(0);
  const [saveEnabled, setSaveEnabled] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [partyUpdateTrigger, setPartyUpdateTrigger] = useState(0);
  const isStakeholderRevampEnabled = useAppSelector(isCreditAccountStakeholdersRevampEnabled);
  const isIncidentParticularsEnabled = useAppSelector(isFICIncidentParticularsEnabled);
  const isDateDisabled = !!ficEvent?.date;
  const isNoFraud = committeeData.decision === FinalFICDecision.NO_FRAUD;

  useEffect(() => {
      if (case_id) {
          dispatch(fetchFinalFICMeetingThunk({ case_id }));
      }
  }, [case_id, dispatch]);

  useEffect(() => {
      setCommitteeData(toCommitteeData(ficData, ficEvent));
  }, [ficData, ficEvent]);

  useEffect(() => {
      setCommiteeRemarks(ficData.review_committee_remarks || "");
      setEditorResetKey(k => k + 1);
  }, [ficData.review_committee_remarks]);

  useEffect(() => {
      setIncident(storedIncident);
  }, [storedIncident]);

  useEffect(() => {
      if (!isNoFraud) return;
      setIncident((current) => ({
          ...current,
          exposure: storedIncident.exposure,
          dateOfOccurrence: storedIncident.dateOfOccurrence,
          dateOfDetection: storedIncident.dateOfDetection,
      }));
  }, [isNoFraud, storedIncident]);

  const hasExposureChanges = (incident.exposure ?? 0) !== (storedIncident.exposure ?? 0);
  const exposureError = hasExposureChanges ? validateExposure(incident.exposure ?? 0) : null;

  const hasOccurrenceChanges = !isNoFraud && isDateChanged(incident.dateOfOccurrence, occurrenceEvent);
  const hasDetectionChanges = !isNoFraud && isDateChanged(incident.dateOfDetection, detectionEvent);
  const hasClassificationChanges = isDateChanged(incident.dateOfClassificationApproval, classificationEvent);

  const hasIncidentChanges =
      hasExposureChanges || hasOccurrenceChanges || hasDetectionChanges || hasClassificationChanges;

  const { hasCommitteeChanges, hasReportChanges, hasAnyChanges } = useMemo(() => {
      const hasCommitteeChanges =
          (committeeData.convener || "") !== (ficData.convener || "") ||
          (committeeData.committeeName || "") !== (ficData.name_of_committee || "") ||
          (committeeData.decision || undefined) !== (ficData.committee_decision || undefined) ||
          (committeeData.fraudClassification || "") !== (ficData.fraud_classification || "") ||
          (committeeData.date || "") !== (ficEvent?.date || "") ||
          JSON.stringify(committeeData.members || []) !== JSON.stringify(ficData.members_of_committee || []);

      const hasRemarksChanges = (commiteeRemarks || "") !== (ficData.review_committee_remarks || "");
      const hasPartyChanges = partiesRef.current?.hasChanges() || false;

      const hasReportChanges = hasCommitteeChanges || hasRemarksChanges || hasIncidentChanges;

      return {
          hasCommitteeChanges,
          hasReportChanges,
          hasAnyChanges: hasReportChanges || hasPartyChanges,
      };
  }, [commiteeRemarks, committeeData, ficData, ficEvent, partyUpdateTrigger, hasIncidentChanges]);

  useEffect(() => {
      const isIncidentValid = !exposureError;

      const isFraudDecision =
          committeeData.decision === FinalFICDecision.FRAUD ||
          committeeData.decision === FinalFICDecision.ATTEMPTED_FRAUD;

      const isCommitteeFilled =
          !!committeeData.decision &&
          (!isFraudDecision || !!committeeData.fraudClassification) &&
          (isDateDisabled || !!committeeData.date);

      const isFirstReportSave = hasReportChanges && !isCreated;
      const isCommitteeRequired = hasCommitteeChanges || isFirstReportSave;
      const isCommitteeValid = isCommitteeFilled || !isCommitteeRequired;

      setSaveEnabled(hasAnyChanges && isIncidentValid && isCommitteeValid);
  }, [hasAnyChanges, hasCommitteeChanges, hasReportChanges, committeeData.decision, committeeData.fraudClassification, committeeData.date, isDateDisabled, isCreated, exposureError]);

  const handleDiscard = () => {
      setCommitteeData(toCommitteeData(ficData, ficEvent));
      setIncident(storedIncident);
      setCommiteeRemarks(ficData.review_committee_remarks || "");
      partiesRef.current?.reset();
      setPartyUpdateTrigger(v => v + 1);
      setEditorResetKey(k => k + 1);
      setSaveEnabled(false);
  };

  const handleSave = async () => {
      if (!case_id) return;
      try {
          const now = new Date().toISOString();

          const toCaseEvent = (event_type: EventType, date = "", storedEvent?: CaseEvent): CaseEvent => ({
              event_id: storedEvent?.event_id ?? uuidv4(),
              event_type,
              actor_email: actorEmail,
              entity_type: EventEntityType.CASE,
              entity_id: case_id,
              date,
              created_at: storedEvent?.created_at ?? now,
              last_updated_at: now,
              status: EventStatus.ACTIVE,
          });

          const incidentEvents: CaseEvent[] = [];

          if (hasOccurrenceChanges) {
              incidentEvents.push(toCaseEvent(EventType.DATE_OF_OCCURENCE, incident.dateOfOccurrence, occurrenceEvent));
          }
          if (hasDetectionChanges) {
              incidentEvents.push(toCaseEvent(EventType.DATE_OF_DETECTION, incident.dateOfDetection, detectionEvent));
          }
          if (hasClassificationChanges) {
              incidentEvents.push(toCaseEvent(EventType.DATE_OF_CLASSIFICATION_CA_APPROVAL, incident.dateOfClassificationApproval, classificationEvent));
          }

          const basePayload = {
              convener: committeeData.convener,
              name_of_committee: committeeData.committeeName,
              members_of_committee: committeeData.members,
              committee_decision: committeeData.decision!,
              fraud_classification: committeeData.fraudClassification,
              review_committee_remarks: commiteeRemarks,
              exposure: hasExposureChanges ? lakhsToRupees(incident.exposure) : undefined,
              events: incidentEvents,
          };
          setIsSaving(true);

          if (hasReportChanges) {
              if (isCreated) {
                  await dispatch(
                  updateFinalFICMeetingThunk({
                      case_id,
                      request: basePayload,
                  })
                  ).unwrap();

              } else {
                  await dispatch(
                  createFinalFICMeetingThunk({
                      case_id,
                      request: {
                      ...basePayload,
                      date_of_committee: ficEvent?.date || committeeData.date,
                      },
                  })
                  ).unwrap();
              }
          }
          await partiesRef.current?.save();

          showSuccessToast("Changes saved successfully");
      } catch (error){
          showErrorToast("Failed to save changes. Please try again.");
      } finally { setIsSaving(false); }
  };

  const handlePartyNotify = useCallback(() => {
    setPartyUpdateTrigger(v => v + 1);
  }, []);

  return (
    <>
      <div className={`${styles.topHeader} flex items-center justify-end`}>
        <ReportActionHeader
          isPageLoading={isFicLoading}
          isSaving={isSaving}
          disableSave={!saveEnabled}
          disableDiscard={!hasAnyChanges}
          handleSave={handleSave}
          handleDiscard={handleDiscard}
        />
      </div>

      <div className={styles.pageContainer}>
        <div className={styles.mainContent}>
          <FICReportCard>
            <ReportSectionHeading index={1} title="Committee Details" />

            <FICDecisionCommitteeTable
              committeeData={committeeData}
              onChange={(data) => setCommitteeData((prev) => ({ ...prev, ...data }))}
            />

            <ReportDocuments
              document_type={DocumentType.COMMITTEE_REPORT}
              entityType={DocumentEntityType.CASE}
              entityId={case_id!}
            />

            <ReportSectionHeading index={2} title="Committee Remarks" />

            <CansoEditor
              id="fic-editor"
              markdown={commiteeRemarks}
              onChange={(value) => setCommiteeRemarks(value || "")}
              readOnly={false}
              className="border border-gray-300 rounded-md"
              contentEditableClassName="h-[250px]"
              key={editorResetKey}
            />

            {isIncidentParticularsEnabled && (
              <>
                <ReportSectionHeading index={3} title="Incident Particulars" />

                <FICIncidentParticularsTable
                  incident={incident}
                  isNoFraud={isNoFraud}
                  exposureError={exposureError}
                  onChange={(changes) => setIncident((prev) => ({ ...prev, ...changes }))}
                />
              </>
            )}

            <ReportSectionHeading
              index={isIncidentParticularsEnabled ? 4 : 3}
              title="Select the parties involved in Fraud"
            />

            {isStakeholderRevampEnabled ? (
              <FICPartiesInvolvedSectionV2 ref={partiesRef} onNotifyChange={handlePartyNotify} />
            ) : (
              <FICPartiesInvolvedSection ref={partiesRef} onNotifyChange={handlePartyNotify} />
            )}

            <div className="mt-7 mb-3">
              <ViewOrAddRecommendation caseId={case_id!} scrollRef={scrollRef} />
            </div>
          </FICReportCard>
        </div>

        <FICPageFooter />
      </div>
    </>
  );
};
